import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState, type FormEvent } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { Button, ErrorText, TextField } from "../../ui";
import { Dialog } from "../../ui/overlay";
import { useToast } from "../../ui/toast";
import { normalizeUrl, type LinkScope, type LinkTile } from "./links-model";

/** Add a link (link === null) or edit / remove one. */
export function LinkDialog({
  scope,
  scopeLabel,
  link,
  groups,
  onClose,
}: {
  scope: LinkScope;
  scopeLabel: string;
  link: LinkTile | null;
  groups: readonly string[];
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const toast = useToast();
  const titleRef = useRef<HTMLInputElement>(null);
  const groupsId = useId();
  const [title, setTitle] = useState(link === null ? "" : link.title);
  const [url, setUrl] = useState(link === null ? "" : link.url);
  const [group, setGroup] = useState(link === null ? "" : link.group);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const finish = async (message: string) => {
    await queryClient.invalidateQueries({ queryKey: trpc.dashboard.links.queryKey({ scope }) });
    toast({ message });
    onClose();
  };
  const add = useMutation(trpc.dashboard.addLink.mutationOptions({ onSuccess: (l) => finish(`Added “${l.title}”.`) }));
  const update = useMutation(trpc.dashboard.updateLink.mutationOptions({ onSuccess: () => finish("Link saved.") }));
  const remove = useMutation(trpc.dashboard.removeLink.mutationOptions({ onSuccess: () => finish("Link removed.") }));
  const busy = add.isPending || update.isPending || remove.isPending;
  const failed = [add, update, remove].find((m) => m.isError);

  function submit(e: FormEvent) {
    e.preventDefault();
    const normalized = normalizeUrl(url);
    if (normalized === null) {
      setUrlError("Enter a web address such as https://example.com.");
      return;
    }
    setUrlError(null);
    const fields = { title: title.trim(), url: normalized, group: group.trim() };
    if (link === null) add.mutate({ scope, ...fields });
    else update.mutate({ scope, id: link.id, ...fields, group: fields.group.length === 0 ? "Links" : fields.group });
  }

  return (
    <Dialog
      title={link === null ? "Add link" : "Edit link"}
      description={scopeLabel}
      onClose={onClose}
      initialFocus={titleRef}
    >
      <form onSubmit={submit} className="flex flex-col gap-4" aria-label={link === null ? "Add link" : "Edit link"}>
        <TextField ref={titleRef} label="Title" required maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} />
        <TextField
          label="URL"
          required
          inputMode="url"
          autoComplete="off"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          aria-invalid={urlError === null ? undefined : true}
        />
        {urlError === null ? null : <ErrorText>{urlError}</ErrorText>}
        <TextField
          label="Group"
          hint="Tiles are grouped by this name; empty means “Links”."
          maxLength={60}
          list={groupsId}
          value={group}
          onChange={(e) => setGroup(e.target.value)}
        />
        <datalist id={groupsId}>
          {groups.map((g) => (
            <option key={g} value={g} />
          ))}
        </datalist>
        <div className="min-h-5">{failed === undefined ? null : <ErrorText>{errorMessage(failed.error)}</ErrorText>}</div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          {link === null ? (
            <span />
          ) : confirmRemove ? (
            <Button variant="danger" disabled={busy} onClick={() => remove.mutate({ scope, id: link.id })}>
              Confirm remove
            </Button>
          ) : (
            <Button variant="danger" onClick={() => setConfirmRemove(true)}>
              Remove
            </Button>
          )}
          <div className="flex gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy || title.trim().length === 0}>
              {link === null ? "Add link" : "Save"}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
